import React from 'react';

const featureGroups = [
  {
    eyebrow: 'SLA Management',
    title: 'Never miss a deadline again',
    desc: 'Define SLA policies per category and priority. ATLAS automatically tracks response and resolution clocks, escalates when thresholds are approaching, and fires breach alerts so your team can act before it\'s too late.',
    bullets: [
      'Configurable SLA policies by ticket type and priority',
      'Real-time countdown timers on every active ticket',
      'Automated escalation chains before breach',
      'SLA breach notifications via email, in-app, and webhook',
      'SLA compliance dashboards with trend reporting',
    ],
    visual: 'sla',
  },
  {
    eyebrow: 'Role-Based Access Control',
    title: 'Right access. Right people. Always.',
    desc: 'Granular RBAC ensures every user sees only what they\'re supposed to. Define roles at the organisation, team, or individual level. Combine with module-level permissions for surgical control.',
    bullets: [
      'Predefined roles: Admin, Agent, Manager, End User',
      'Custom role builder with module-level permissions',
      'Multi-team and multi-department scoping',
      'Audit log of every permission change',
      'SSO-ready with Active Directory integration',
    ],
    visual: 'rbac',
  },
  {
    eyebrow: 'Smart Notifications',
    title: 'The right alert, to the right person',
    desc: 'Configurable notification rules triggered by ticket events, SLA milestones, or status changes. No more noise — agents get exactly what they need to act, nothing more.',
    bullets: [
      'Event-driven triggers: create, update, resolve, escalate',
      'Email, in-app, and webhook delivery channels',
      'Per-user notification preferences',
      'Digest mode for low-urgency updates',
      'Notification history and delivery status tracking',
    ],
    visual: 'notif',
  },
];

const MockSLA = () => (
  <div style={{ background: '#0f1628', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', minHeight: '220px' }}>
    <div style={{ fontSize: '11px', color: '#5a6a8a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>SLA Status</div>
    {[
      { id: 'INC-4821', label: 'VPN Outage', pct: 82, color: '#ef4444', time: '38 min left' },
      { id: 'SR-4820', label: 'Laptop Request', pct: 45, color: '#fbbf24', time: '4h 20m left' },
      { id: 'SR-4819', label: 'Email Setup', pct: 12, color: '#52b788', time: '9h 05m left' },
    ].map(r => (
      <div key={r.id} style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: '#fff', fontWeight: 600 }}>{r.id} <span style={{ color: '#a8b4cc', fontWeight: 400 }}>· {r.label}</span></span>
          <span style={{ fontSize: '11px', color: r.color, fontWeight: 600 }}>{r.time}</span>
        </div>
        <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px' }}>
          <div style={{ height: '100%', width: `${r.pct}%`, background: r.color, borderRadius: '3px', transition: 'width 1s' }} />
        </div>
      </div>
    ))}
    <div style={{ marginTop: '20px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '14px' }}>🔔</span>
      <span style={{ fontSize: '12px', color: '#fca5a5' }}>SLA breach alert sent for INC-4821</span>
    </div>
  </div>
);

const MockRBAC = () => (
  <div style={{ background: '#0f1628', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', minHeight: '220px' }}>
    <div style={{ fontSize: '11px', color: '#5a6a8a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Role Permissions</div>
    {[
      { role: 'Admin', modules: ['All Modules'], color: '#52b788' },
      { role: 'ITSM Manager', modules: ['Incidents', 'Changes', 'Reports'], color: '#60a5fa' },
      { role: 'Level 1 Agent', modules: ['Service Requests', 'Incidents'], color: '#a78bfa' },
      { role: 'End User', modules: ['My Tickets', 'Knowledge Base'], color: '#fbbf24' },
    ].map(r => (
      <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', padding: '10px 12px', background: 'rgba(255,255,255,0.025)', borderRadius: '8px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${r.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: r.color }}>
          {r.role[0]}
        </div>
        <div>
          <div style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>{r.role}</div>
          <div style={{ fontSize: '11px', color: '#5a6a8a' }}>{r.modules.join(' · ')}</div>
        </div>
        <div style={{ marginLeft: 'auto', width: '8px', height: '8px', borderRadius: '50%', background: r.color }} />
      </div>
    ))}
  </div>
);

const MockNotif = () => (
  <div style={{ background: '#0f1628', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', minHeight: '220px' }}>
    <div style={{ fontSize: '11px', color: '#5a6a8a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Notification Feed</div>
    {[
      { icon: '⚡', text: 'SLA breach in 30 min: INC-4821', time: 'just now', color: '#ef4444' },
      { icon: '✅', text: 'SR-4819 resolved by Swati Agarwal', time: '2m ago', color: '#52b788' },
      { icon: '🔄', text: 'CHG-0082 approved by CAB', time: '15m ago', color: '#60a5fa' },
      { icon: '📋', text: 'New service request SR-4823 assigned to you', time: '1h ago', color: '#a78bfa' },
      { icon: '⚠️', text: 'P1 Incident INC-4821 opened by system', time: '1h ago', color: '#fbbf24' },
    ].map((n, i) => (
      <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '14px', marginTop: '1px' }}>{n.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: 1.4 }}>{n.text}</div>
          <div style={{ fontSize: '10px', color: '#5a6a8a', marginTop: '2px' }}>{n.time}</div>
        </div>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: n.color, marginTop: '4px', flexShrink: 0 }} />
      </div>
    ))}
  </div>
);

const visuals = { sla: <MockSLA />, rbac: <MockRBAC />, notif: <MockNotif /> };

export default function Features() {
  return (
    <section id="features" style={{ padding: '100px 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div style={{ display: 'inline-block', background: 'rgba(45,106,79,0.12)', border: '1px solid rgba(82,183,136,0.2)', borderRadius: '100px', padding: '5px 16px', fontSize: '12px', color: '#52b788', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '20px' }}>
            Platform Features
          </div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(32px,4vw,52px)', fontWeight: 800, letterSpacing: '-1.5px', marginBottom: '16px' }}>
            Built for enterprise-grade delivery
          </h2>
          <p style={{ fontSize: '17px', color: '#a8b4cc', maxWidth: '520px', margin: '0 auto' }}>
            ATLAS ITSM isn't just a ticketing tool — it's a governance layer for your IT operations.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '80px' }}>
          {featureGroups.map((f, i) => (
            <div key={f.eyebrow} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }} className="feature-row">
              {/* Text left on even, right on odd */}
              {i % 2 === 0 ? (
                <>
                  <div>
                    <div style={{ fontSize: '12px', color: '#52b788', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>{f.eyebrow}</div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(24px,3vw,36px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '16px', lineHeight: 1.15 }}>{f.title}</h3>
                    <p style={{ color: '#a8b4cc', fontSize: '16px', lineHeight: 1.7, marginBottom: '28px' }}>{f.desc}</p>
                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {f.bullets.map(b => (
                        <li key={b} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: '#c8d4e8' }}>
                          <span style={{ color: '#52b788', fontWeight: 700, flexShrink: 0 }}>✓</span> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>{visuals[f.visual]}</div>
                </>
              ) : (
                <>
                  <div className="visual-first">{visuals[f.visual]}</div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#52b788', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' }}>{f.eyebrow}</div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(24px,3vw,36px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '16px', lineHeight: 1.15 }}>{f.title}</h3>
                    <p style={{ color: '#a8b4cc', fontSize: '16px', lineHeight: 1.7, marginBottom: '28px' }}>{f.desc}</p>
                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {f.bullets.map(b => (
                        <li key={b} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: '#c8d4e8' }}>
                          <span style={{ color: '#52b788', fontWeight: 700, flexShrink: 0 }}>✓</span> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:900px){ .feature-row{ grid-template-columns:1fr !important; } .visual-first{ order:-1; } }`}</style>
    </section>
  );
}
